// @ts-nocheck - MUI does not play well with typescript
import { $Shape } from 'utility-types';
import { createTheme as createMuiTheme } from '@material-ui/core/styles';

import { alpha } from '@material-ui/core/styles/colorManipulator';
import shadows from '@material-ui/core/styles/shadows';

import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';

import { getMuiButtonStyle } from '@admin/shared/themes/default/button';
import { getMuiCssBaselineStyle } from '@admin/shared/themes/default/baseline';
import { getMuiIconButtonStyle } from '@admin/shared/themes/default/icon-button';
import { getMuiTabsStyle } from '@admin/shared/themes/default/tabs';
import { getMuiTabStyle } from '@admin/shared/themes/default/tab';
import { getMuiInputBaseStyle } from '@admin/shared/themes/default/input-base';
import { getMuiSelectStyle } from '@admin/shared/themes/default/select';
import { getMuiOutlinedInputStyle } from '@admin/shared/themes/default/outlined-input';
import { getMuiListStyle } from '@admin/shared/themes/default/list';
import { getMuiListItemStyle } from '@admin/shared/themes/default/list-item';
import { getMuiMenuItemStyle } from '@admin/shared/themes/default/menu-item';
import { getMuiSwitchStyle } from '@admin/shared/themes/default/switch';
import { getMuiFormControlLabelStyle } from '@admin/shared/themes/default/form-control-label';
import { getMuiInputAdornmentStyle } from '@admin/shared/themes/default/input-adornment';
import { getMuiTooltipStyle } from '@admin/shared/themes/default/tooltip';
import { getMuiRadioStyle } from '@admin/shared/themes/default/radio';
import { getMuiInputLabelStyle } from '@admin/shared/themes/default/input-label';
import { getMuiFormHelperText } from '@admin/shared/themes/default/form-helper-text';
import { getMuiTreeItemStyle } from '@admin/shared/themes/default/tree-item';
import { getMuiTypographyStyle } from '@admin/shared/themes/default/typography';
import { getMuiCheckboxStyle } from '@admin/shared/themes/default/checkbox';
import { getMuiPaperStyle } from '@admin/shared/themes/default/paper';
import { getMuiPopoverStyle } from '@admin/shared/themes/default/popover';
import { Themes } from '@admin/shared/themes/themes';

const colors = {
  grey1: '#f5f5f7', // mui grey 100
  grey2: '#efefef', // mui grey 200
  grey3: '#a7a7a7', // mui grey 300
  grey4: '#777777', // mui grey 600
  red: '#e70000',
};

const brandColor = '#ff6600';

// OMQ product colors
export const productColors = {
  default: brandColor,
  contact: '#FD1266',
  help: '#CF148F',
  assist: '#9818B2',
  reply: '#7037DD',
  chatbot: '#414CF9',
  users: '#53a064',
  knowledge: brandColor,
  organization: '#c3c72a',
  styles: '#00a4b2',
  reports: '#0070a8',
  automator: '#D11010',
};

/**
 * Type of mui theme.
 */
export type Theme = {
  spacing: number;
  breakpoints: {
    up: (bp: string) => string;
    down: (bp: string) => string;
  };
  palette: {
    common?: {
      black: string;
      white: string;
    };
    primary?: Record<string, string>;
    text?: Record<string, string>;
    icon?: Record<string, string>;
    error?: {
      main: string;
    };
    grey?: Record<string, string>;
    [key: string]: any;
  };
  shadows: Array<string> & {
    default: string;
    top: string;
    bottom: string;
  };
  custom?: {
    colors: Record<string, string>;
  };
  shape: {
    borderRadius: number;
  };
  typography: ThemeStyle;
  props: {
    productColors?: ThemeStyle;
    [key: string]: any;
  };
  overrides: {};
};

/**
 * Type of theme style.
 */
export type ThemeStyle = Record<string, unknown>;

// sometimes, it is required to create component styles with `makeStyles` (material-ui)
// and also export them, to use them in other components as well.
// in flow, all exported objects need to have a type.
// use this type if you need to export the generated hook from makeStyles
export type MakeStyleHookType = () => Record<string, string>;

const defaultFont = '"Roboto", "Helvetica", "Arial", sans-serif';

type ThemeProps = {
  primary?: string;
  font?: string;
};
const defaultThemeProps = {
  primary: brandColor,
  font: defaultFont,
};

export const createTheme = (
  { primary, font = defaultFont }: ThemeProps = defaultThemeProps,
  themeOpts?: $Shape<Theme>,
): Theme => {
  const customShadows: any = [...shadows];
  customShadows.default = 'rgb(0 0 0 / 24%) 0px 8px 20px';
  customShadows.top = '0px -10px 10px 0px rgba(0, 0, 0, .05)';
  customShadows.bottom = '0px 10px 10px 0px rgba(0, 0, 0, .05)';

  // create mui theme with default values
  const theme = createMuiTheme(
    {
      spacing: 5,
      palette: {
        primary: {
          main: primary,
          contrastText: 'rgba(255, 255, 255, 1.0)',
          dark: alpha(primary, 0.92),
        },
        error: {
          main: colors.red,
          warn: '#EE9B00',
        },
        grey: {
          '100': colors.grey1,
          '200': colors.grey2,
          '300': colors.grey3,
          '350': colors.grey3,
          '400': colors.grey4,
          '600': colors.grey4,
        },

        background: {
          default: 'rgba(255, 255, 255, 1.0)',
          transparentWhite: 'rgba(255, 255, 255, 0.6)',
        },

        text: {
          primary: '#000',
        },

        icon: {
          primary: '#000',
        },

        userSelect: {
          primary: '#0d8cff',
        },
      },

      shape: {
        borderRadius: 6,
      },

      shadows: customShadows,

      typography: {
        fontFamily: font,
        fontSize: 14,
        lineHeight: '18px',
      },

      props: {
        productColors,
        meta: {
          name: Themes.OMQ,
        },
      },
    },
    themeOpts,
  );

  // override Mui component styles
  theme.overrides = {
    MuiCssBaseline: getMuiCssBaselineStyle(theme),
    MuiCheckbox: getMuiCheckboxStyle(theme),
    MuiButton: getMuiButtonStyle(theme),
    MuiIconButton: getMuiIconButtonStyle(theme),
    MuiTabs: getMuiTabsStyle(),
    MuiTab: getMuiTabStyle(theme),
    MuiInputBase: getMuiInputBaseStyle(theme),
    MuiSelect: getMuiSelectStyle(theme),
    MuiOutlinedInput: getMuiOutlinedInputStyle(theme),
    MuiList: getMuiListStyle(),
    MuiListItem: getMuiListItemStyle(theme),
    MuiMenuItem: getMuiMenuItemStyle(),
    MuiSwitch: getMuiSwitchStyle(theme),
    MuiFormControlLabel: getMuiFormControlLabelStyle(),
    MuiInputAdornment: getMuiInputAdornmentStyle(),
    MuiTooltip: getMuiTooltipStyle(theme),
    MuiRadio: getMuiRadioStyle(),
    MuiInputLabel: getMuiInputLabelStyle(theme),
    MuiFormHelperText: getMuiFormHelperText(theme),
    MuiTreeItem: getMuiTreeItemStyle(theme),
    MuiTypography: getMuiTypographyStyle(theme),
    MuiPopover: getMuiPopoverStyle(),
    MuiPaper: getMuiPaperStyle(theme),
  };

  return theme;
};
